import { z } from 'zod';
import { Scenarios } from './enums';
import { idSchema } from './ids';
import { amountInSchema, type Cents, percentOf, sumCents, toCents } from './money';
import { compareYearMonth, type YearMonth } from './period';
import { profitMargin } from './format';

/**
 * Plan / Fact timesheets of a project (FR-TS-001…019): months with typed amounts, resource rows
 * with a rate and hours per month. Every derived figure (amounts, totals, profit, margin, the
 * financial summary) is computed here — the project card, the overview, turnover and the API use
 * the same functions.
 */

/** Amounts typed per month (FR-TS-010/011); the order is the row order in the sheet. */
export const MONTH_AMOUNTS = [
  'licenceCosts',
  'invoicedAdvance',
  'advanceBudgetLeft',
  'advanceSubtracted',
  'invoiced',
  'invoicePlanned',
  'projectRevenue',
] as const;
export type MonthAmount = (typeof MONTH_AMOUNTS)[number];
/** The financial rows below "Total Costs" (FR-TS-011). */
export const FINANCIAL_AMOUNTS = MONTH_AMOUNTS.filter((k) => k !== 'licenceCosts');

export const MAX_SHEET_MONTHS = 120;
export const MAX_SHEET_ROWS = 200;
const hoursSchema = z.number().min(0).max(10_000);
/** Money in a sheet as the API returns it; `null` when the reader may not see finance data. */
const amountOutSchema = z.string().nullable();

const monthFields = { year: z.number().int().min(2000).max(2100), month: z.number().int().min(1).max(12) };

export const timesheetMonthSchema = z.object({
  ...monthFields,
  locked: z.boolean(),
  ...(Object.fromEntries(MONTH_AMOUNTS.map((k) => [k, amountOutSchema])) as Record<
    MonthAmount,
    typeof amountOutSchema
  >),
});
export type TimesheetMonth = z.infer<typeof timesheetMonthSchema>;

export const timesheetRowSchema = z.object({
  /** null — a row not saved yet. */
  id: idSchema.nullable(),
  resourceId: idSchema.nullable(),
  name: z.string(),
  rate: amountOutSchema,
  /** Hours per month, aligned with `months`. */
  hours: z.array(z.number()),
});
export type TimesheetRow = z.infer<typeof timesheetRowSchema>;

export const timesheetSchema = z.object({
  scenario: Scenarios.schema,
  months: z.array(timesheetMonthSchema),
  rows: z.array(timesheetRowSchema),
  /** "Additional Expenses (%)" (FR-TS-009). */
  additionalPct: z.number(),
  /** 0 — never saved (the default sheet, FR-TS-002). */
  version: z.number().int(),
  /** Last change of this sheet (FR-TS-019). */
  updatedAt: z.string().nullable(),
});
export type Timesheet = z.infer<typeof timesheetSchema>;

export const projectTimesheetsSchema = z.object({ plan: timesheetSchema, fact: timesheetSchema });
export type ProjectTimesheets = z.infer<typeof projectTimesheetsSchema>;

/** What the card saves (one scenario at a time, FR-TS-013). */
export const timesheetInputSchema = z
  .object({
    months: z
      .array(
        z.object({
          ...monthFields,
          locked: z.boolean(),
          ...(Object.fromEntries(MONTH_AMOUNTS.map((k) => [k, amountInSchema])) as Record<
            MonthAmount,
            typeof amountInSchema
          >),
        }),
      )
      .min(1, { message: 'Jābūt vismaz vienam mēnesim.' })
      .max(MAX_SHEET_MONTHS),
    rows: z
      .array(
        z.object({
          id: idSchema.nullable(),
          resourceId: idSchema.nullable(),
          name: z.string().trim().max(200),
          rate: amountInSchema,
          hours: z.array(hoursSchema),
        }),
      )
      .min(1, { message: 'Jābūt vismaz vienam resursam.' })
      .max(MAX_SHEET_ROWS),
    additionalPct: z.number().min(0).max(1000),
    version: z.number().int().min(0),
  })
  .superRefine((sheet, ctx) => {
    sheet.months.forEach((m, i) => {
      if (i > 0 && compareYearMonth(sheet.months[i - 1]!, m) >= 0)
        ctx.addIssue({ code: 'custom', path: ['months', i], message: 'Mēnešiem jābūt augošā secībā.' });
    });
    sheet.rows.forEach((r, i) => {
      if (r.hours.length !== sheet.months.length)
        ctx.addIssue({
          code: 'custom',
          path: ['rows', i, 'hours'],
          message: 'Stundām jābūt katram mēnesim.',
        });
    });
  });
export type TimesheetInput = z.input<typeof timesheetInputSchema>;

export const scenarioSchema = Scenarios.schema;

// ───────────────────────────── calculations ─────────────────────────────

/** What the calculations need — a saved sheet, the default one or the one being edited. */
export interface SheetData {
  months: readonly (YearMonth & Partial<Record<MonthAmount, string | null>>)[];
  rows: readonly { rate: string | null; hours: readonly number[] }[];
  additionalPct: number;
}

export interface Series {
  /** Per month, aligned with the sheet's months. */
  values: number[];
  total: number;
}

const series = (values: number[]): Series => ({ values, total: values.reduce((s, v) => s + v, 0) });

export interface SheetFigures {
  /** Per resource row: amount = hours × rate (FR-TS-007), in cents. */
  rows: { amounts: Series; hours: Series }[];
  /** "Total Expenses (Developers & PM)" (FR-TS-008). */
  expenses: { amounts: Series; hours: Series };
  /** "Additional Expenses" = expenses × % (FR-TS-009). */
  additional: Series;
  licence: Series;
  /** "Total Costs" (FR-TS-010). */
  costs: Series;
  /** Typed financial rows (FR-TS-011), cents. */
  amounts: Record<MonthAmount, Series>;
  /** Profit = Invoiced − Total Costs (FR-TS-012). */
  profit: Series;
  /** Profit Margin % = Invoiced / Total Costs (Q14); the total is computed from the totals. */
  margin: { values: number[]; total: number };
}

const rowAmount = (rate: string | null, hours: number): Cents => Math.round(hours * toCents(rate));

export function computeSheet(sheet: SheetData): SheetFigures {
  const n = sheet.months.length;
  const at = <T>(fn: (i: number) => T) => Array.from({ length: n }, (_, i) => fn(i));
  const rows = sheet.rows.map((r) => ({
    amounts: series(at((i) => rowAmount(r.rate, r.hours[i] ?? 0))),
    hours: series(at((i) => r.hours[i] ?? 0)),
  }));
  const expenses = {
    amounts: series(at((i) => sumCents(rows.map((r) => r.amounts.values[i]!)))),
    hours: series(at((i) => rows.reduce((s, r) => s + r.hours.values[i]!, 0))),
  };
  const additional = series(expenses.amounts.values.map((c) => Math.round((c * sheet.additionalPct) / 100)));
  const amounts = Object.fromEntries(
    MONTH_AMOUNTS.map((k) => [k, series(at((i) => toCents(sheet.months[i]![k])))]),
  ) as Record<MonthAmount, Series>;
  const licence = amounts.licenceCosts;
  const costs = series(at((i) => expenses.amounts.values[i]! + additional.values[i]! + licence.values[i]!));
  const profit = series(at((i) => amounts.invoiced.values[i]! - costs.values[i]!));
  const margin = {
    values: at((i) => profitMargin(amounts.invoiced.values[i]!, costs.values[i]!)),
    total: profitMargin(amounts.invoiced.total, costs.total),
  };
  return { rows, expenses, additional, licence, costs, amounts, profit, margin };
}

/** One column of the "Financial Summary" (FR-TS-018): a year or the total. */
export interface SummaryColumn {
  year: number | null;
  hours: number;
  /** = Total Costs. */
  expenses: Cents;
  invoicedAdvance: Cents;
  invoiced: Cents;
  revenue: Cents;
  balance: Cents;
  toInvoice: Cents;
  invoicedPct: number;
  profit: Cents;
  margin: number;
}

export function summarizeSheet(sheet: SheetData): SummaryColumn[] {
  const f = computeSheet(sheet);
  const column = (year: number | null, indices: number[]): SummaryColumn => {
    const sum = (s: Series) => indices.reduce((t, i) => t + s.values[i]!, 0);
    const expenses = sum(f.costs);
    const invoiced = sum(f.amounts.invoiced);
    const revenue = sum(f.amounts.projectRevenue);
    return {
      year,
      hours: indices.reduce((t, i) => t + f.expenses.hours.values[i]!, 0),
      expenses,
      invoicedAdvance: sum(f.amounts.invoicedAdvance),
      invoiced,
      revenue,
      balance: invoiced - expenses,
      toInvoice: revenue - invoiced,
      invoicedPct: percentOf(invoiced, revenue),
      profit: invoiced - expenses,
      margin: profitMargin(invoiced, expenses),
    };
  };
  const years = [...new Set(sheet.months.map((m) => m.year))].sort((a, b) => a - b);
  const all = sheet.months.map((_, i) => i);
  return [
    ...years.map((y) =>
      column(
        y,
        all.filter((i) => sheet.months[i]!.year === y),
      ),
    ),
    column(null, all),
  ];
}

/** A month column holds data: hours, licence costs or financial amounts above 0 (FR-TS-004). */
export function monthHasData(sheet: SheetData, index: number): boolean {
  const month = sheet.months[index];
  if (!month) return false;
  return (
    sheet.rows.some((r) => (r.hours[index] ?? 0) > 0) || MONTH_AMOUNTS.some((k) => toCents(month[k]) > 0)
  );
}

/** Anything worth keeping: data in a month or a rate above 0 (FR-TS-015 "overwrite" check). */
export function sheetHasData(sheet: SheetData): boolean {
  return sheet.months.some((_, i) => monthHasData(sheet, i)) || sheet.rows.some((r) => toCents(r.rate) > 0);
}

/** The card's (or a test's) sheet → what is saved; hidden money (null) is sent as 0. */
export function timesheetToInput(
  sheet: Pick<Timesheet, 'months' | 'rows' | 'additionalPct' | 'version'>,
): TimesheetInput {
  return {
    months: sheet.months.map((m) => ({
      year: m.year,
      month: m.month,
      locked: m.locked,
      ...(Object.fromEntries(MONTH_AMOUNTS.map((k) => [k, m[k] ?? '0.00'])) as Record<MonthAmount, string>),
    })),
    rows: sheet.rows.map((r) => ({
      id: r.id,
      resourceId: r.resourceId,
      name: r.name,
      rate: r.rate ?? '0.00',
      hours: [...r.hours],
    })),
    additionalPct: sheet.additionalPct,
    version: sheet.version,
  };
}
